/**
 * App Connection — caller-model address grammar.
 *
 * Addresses appear in cloudgrid.yaml under `calls:` and
 * `callers.allowed_apps:`. The platform identifies entities by entity_id at
 * the grant lifecycle layer; the user-facing yaml refers to them by one of
 * three grid-based forms:
 *
 *   <app-slug>               an app in the caller's own grid (the common case)
 *   <grid-slug>/<app-slug>   an app in a named grid (same-grid only in this
 *                            release; cross-grid needs the co-sign flow)
 *   <entity-id>              a UUID, unambiguous across renames
 *
 * The old `org:<slug>/<entity>` and `space:<slug>/<entity>` schemes are gone
 * (clean break — App Connection never worked end-to-end, so there is nothing
 * deployed to migrate). The `space:` ADDRESS forms were vestigial: the space
 * slug was decorative and never resolved. Space-membership auto-granting
 * lives on as `callers.scope: 'space'`, which is unrelated to address syntax.
 *
 * The grammar is transport-independent (decision 051): `parseCallerAddress`
 * validates the syntax and splits it into parts, and that is all this module
 * does. The env-var-name encoders that used to live here
 * (`callerTokenEnvVar`/`callerUrlEnvVar`) died with the token transport —
 * nothing is injected into pods; `runtime.call` resolves addresses at call
 * time via `/runtime/app-connection/resolve`.
 */

// Parse order is UUID → slash → bare: a UUID is lexically a valid slug, so
// the id check must run first.
const ENTITY_ID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const GRID_APP_RE = /^([a-z0-9-]+)\/([a-z0-9-]+)$/;
// The standard DNS-1123 / decision 010 slug shape. Length bounds are not
// enforced here (the entity lookup at deploy time rejects any slug the
// system never produced).
const SLUG_RE = /^[a-z0-9-]+$/;

export type CallerAddress =
  | { form: 'slug'; entitySlug: string; raw: string }
  | { form: 'grid'; gridSlug: string; entitySlug: string; raw: string }
  | { form: 'id'; entityId: string; raw: string };

/**
 * Parse a caller-model address. Returns `null` when the input does not match
 * the supported syntaxes — callers decide what error to surface.
 */
export function parseCallerAddress(addr: string): CallerAddress | null {
  if (typeof addr !== 'string') return null;

  if (ENTITY_ID_RE.test(addr)) {
    return { form: 'id', entityId: addr, raw: addr };
  }

  const gridMatch = GRID_APP_RE.exec(addr);
  if (gridMatch) {
    return { form: 'grid', gridSlug: gridMatch[1], entitySlug: gridMatch[2], raw: addr };
  }

  if (SLUG_RE.test(addr)) {
    return { form: 'slug', entitySlug: addr, raw: addr };
  }

  return null;
}
